"use client";
import { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import Link from "next/link";
import { FcGoogle } from "react-icons/fc";
import {
  useRegister,
  useRegisterAndEnroll,
  useLogin,
} from "@/api/hook/useAuth";
import {
  getPendingEnrollCourseId,
  clearPendingEnrollCourseId,
} from "@/api/utils/session";
import { getUserLocation } from "@/api/utils/location";
import * as Yup from "yup";
import Header from "@/components/layout/Header";
import { useGoogleAuth } from "@/api/hook/useGoogleAuth";
import { Eye, EyeOff, CheckCircle } from "lucide-react";
import { ExistingEmailModal } from "@/components/ExistingEmailModal";
import { useSearchParams, useRouter } from "next/navigation";
import toast from "react-hot-toast";

const SignUpPage = () => {
  const searchParams = useSearchParams();
  const router = useRouter();

  const courseIdParam = searchParams.get("courseId");
  const memberNoParam = searchParams.get("memberNo");
  const isFromCourseLogin = !!(courseIdParam && memberNoParam);

  const [showExistingEmailModal, setShowExistingEmailModal] = useState(false);
  const [existingEmail, setExistingEmail] = useState("");

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [registeredPassword, setRegisteredPassword] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const loginMutation = useLogin();

  const handleRegisterError = (error: any) => {
    if (error?.response?.status === 502) {
      let formEmail = "";
      if (error?.response?.data?.email) {
        formEmail = error.response.data.email;
      } else if (error?.config?.data) {
        try {
          const requestData = JSON.parse(error.config.data);
          formEmail = requestData.email || "";
        } catch (e) {
          console.error("Failed to parse request data:", e);
        }
      }
      setExistingEmail(formEmail);
      setShowExistingEmailModal(true);
    }
  };

  const handleRegistrationSuccess = (email: string, password: string) => {
    setRegisteredEmail(email);
    setRegisteredPassword(password);
    setShowSuccessModal(true);
  };

  const handleLoginAfterRegistration = async () => {
    setIsLoggingIn(true);
    try {
      await loginMutation.mutateAsync({
        email: registeredEmail,
        password: registeredPassword,
      });
      toast.success("Login successful! Redirecting to dashboard...");
      router.push("/dashboard");
      router.refresh();
    } catch (error) {
      console.error("Auto-login failed:", error);
      toast.error("Login failed. Please sign in manually.");
      router.push("/sign-in");
    } finally {
      setIsLoggingIn(false);
      setShowSuccessModal(false);
    }
  };

  const registerMutation = useRegister(
    (data?: { token?: string; email?: string }) => {
      toast.success("Account created successfully!");
    },
    handleRegisterError,
  );

  const registerAndEnrollMutation = useRegisterAndEnroll(
    (data?: { token?: string; email?: string }) => {
      toast.success("Account created successfully!");
    },
    handleRegisterError,
  );

  const [showPassword, setShowPassword] = useState(false);
  const [location, setLocation] = useState({ country: "", countryState: "" });
  const { signUpWithGoogle, isLoading: isGoogleLoading } = useGoogleAuth();

  useEffect(() => {
    const fetchLocation = async () => {
      const userLocation = await getUserLocation();
      setLocation(userLocation);
    };
    fetchLocation();
  }, []);

  const handleGoogleSignUp = async () => {
    try {
      await signUpWithGoogle(location);
    } catch (error) {
      console.error("Google sign up error:", error);
    }
  };

  return (
    <>
      <Header />
      <div className="flex flex-col lg:flex-row min-h-screen">
        {/* Left Section */}
        <div className="hidden md:flex w-full lg:w-2/5 bg-[#1C1B4D] flex-col justify-center px-6 sm:px-10 lg:px-16 xl:px-20 py-12">
          <h1 className="font-bold text-[#FFF] text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl">
            Start learning with
          </h1>
          <h1
            className="font-bold text-2xl sm:text-4xl md:text-5xl lg:text-6xl mt-2 text-center lg:text-left"
            style={{
              backgroundImage: "linear-gradient(90deg, #EFAE33, #B97BFF)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
              display: "inline-block",
            }}
          >
            La Plage Meta Verse
          </h1>
        </div>

        {/* Right Section */}
        <div className="w-full lg:w-3/5 flex justify-center items-center px-6 sm:px-10 py-12">
          <div className="w-full max-w-md">
            <h3 className="font-bold text-primary text-3xl sm:text-4xl text-center mb-8">
              Sign Up.
            </h3>

            <Formik
              initialValues={{
                fullName: "",
                email: "",
                password: "",
              }}
              validationSchema={Yup.object().shape({
                fullName: Yup.string()
                  .required("Full name is required")
                  .min(2, "Full name must be at least 2 characters"),
                email: Yup.string()
                  .email("Invalid email address")
                  .required("Email is required"),
                password: Yup.string()
                  .min(6, "Password must be at least 6 characters")
                  .required("Password is required"),
              })}
              onSubmit={async (values, { setSubmitting, setFieldError }) => {
                const nameParts = values.fullName.trim().split(/\s+/);
                const registrationData = {
                  firstName: nameParts[0],
                  middleName:
                    nameParts.length > 2
                      ? nameParts.slice(1, -1).join(" ")
                      : "",
                  lastName:
                    nameParts.length > 1 ? nameParts[nameParts.length - 1] : "",
                  email: values.email,
                  password: values.password,
                  country: location.country,
                  countryState: location.countryState,
                };

                try {
                  if (isFromCourseLogin) {
                    await registerAndEnrollMutation.mutateAsync({
                      userData: registrationData,
                      courseId: Number(courseIdParam),
                      memberNo: memberNoParam,
                    });
                  } else {
                    const pendingCourseId = getPendingEnrollCourseId();

                    if (pendingCourseId) {
                      await registerAndEnrollMutation.mutateAsync({
                        userData: registrationData,
                        courseId: pendingCourseId,
                      });
                      // Already enrolled; clear so the auto-login doesn't enroll a second time
                      clearPendingEnrollCourseId();
                    } else {
                      await registerMutation.mutateAsync(registrationData);
                    }
                  }

                  handleRegistrationSuccess(values.email, values.password);
                } catch (error: any) {
                  if (error?.response?.status === 502) {
                    setFieldError("email", "Email already exists");
                  } else {
                    toast.error(
                      error?.response?.data ||
                        "Registration failed. Please try again.",
                    );
                  }
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({ isSubmitting, errors, touched }) => (
                <Form className="flex flex-col gap-6">
                  <div className="flex flex-col gap-3">
                    <label className="font-medium text-lg sm:text-xl text-primary">
                      Full Name
                    </label>
                    <Field
                      type="text"
                      name="fullName"
                      className={`rounded-md py-3 px-4 border ${
                        errors.fullName && touched.fullName
                          ? "border-[#d30a0a]"
                          : "border-[#111]"
                      }`}
                    />
                    <ErrorMessage
                      name="fullName"
                      component="div"
                      className="text-[#d30a0a] text-sm"
                    />
                  </div>

                  <div className="flex flex-col gap-3">
                    <label className="font-medium text-lg sm:text-xl text-primary">
                      Email Address
                    </label>
                    <Field
                      type="email"
                      name="email"
                      className={`rounded-md py-3 px-4 border ${
                        errors.email && touched.email
                          ? "border-[#d30a0a]"
                          : "border-[#111]"
                      }`}
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="text-[#d30a0a] text-sm"
                    />
                  </div>

                  <div className="flex flex-col gap-3">
                    <label className="font-medium text-lg sm:text-xl text-primary">
                      Password
                    </label>
                    <div className="relative">
                      <Field
                        type={showPassword ? "text" : "password"}
                        name="password"
                        className={`rounded-md py-3 px-4 border w-full ${
                          errors.password && touched.password
                            ? "border-[#d30a0a]"
                            : "border-[#111]"
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#6B7280] hover:text-gray-700"
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    <ErrorMessage
                      name="password"
                      component="div"
                      className="text-[#d30a0a] text-sm"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={
                      isSubmitting ||
                      registerMutation.isPending ||
                      registerAndEnrollMutation.isPending
                    }
                    className="bg-primary rounded-md text-[#FFF] text-lg sm:text-xl font-bold py-3 hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {registerMutation.isPending ||
                    registerAndEnrollMutation.isPending
                      ? "Signing Up..."
                      : "Register for FREE"}
                  </button>

                  <p className="font-medium text-lg sm:text-xl text-center">
                    or
                  </p>

                  <div className="flex flex-col gap-3">
                    <button
                      type="button"
                      onClick={handleGoogleSignUp}
                      disabled={isGoogleLoading}
                      className="border rounded-md flex items-center py-3 px-4 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <FcGoogle className="w-5 h-5" />
                      <p className="text-center flex-1">
                        {isGoogleLoading
                          ? "Connecting..."
                          : "Continue with Google"}
                      </p>
                    </button>
                  </div>

                  <p className="text-lg sm:text-xl text-center">
                    Already on La Plage Metaverse?{" "}
                    <Link
                      href={
                        isFromCourseLogin
                          ? `/course-registration/${memberNoParam}/${courseIdParam}`
                          : "/sign-in"
                      }
                      className="font-medium text-primary hover:text-primary/80"
                    >
                      Log In
                    </Link>
                  </p>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>

      {/* 
        Plain HTML success modal — no shadcn Dialog components.
        The backdrop is INSIDE the showSuccessModal conditional so it
        never renders (and never blocks clicks) when the modal is closed.
      */}
      {showSuccessModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="success-modal-title"
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-[#111]/70" />

          {/* Modal card */}
          <div className="relative z-10 bg-[#FFF] rounded-xl shadow-2xl w-full max-w-md mx-4 p-8">
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <CheckCircle className="w-16 h-16 text-[#10B981]" />
            </div>

            {/* Title */}
            <h2
              id="success-modal-title"
              className="text-2xl font-bold text-center text-primary mb-2"
            >
              Account Created Successfully! 🎉
            </h2>

            {/* Description */}
            <p className="text-center text-[#6B7280] mb-6">
              Your account has been created. Please review your login
              credentials below carefully. You will need these details to log in
              next time.
            </p>

            {/* Credentials box */}
            <div className="bg-[#F3F4F6] rounded-lg p-4 mb-6 space-y-3">
              <div>
                <p className="text-sm font-medium text-[#6B7280]">
                  Email Address
                </p>
                <p className="text-base font-semibold text-gray-900 mt-1 break-all">
                  {registeredEmail}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-[#6B7280]">Password</p>
                <p className="text-base font-semibold text-gray-900 mt-1">
                  {registeredPassword}
                </p>
              </div>
            </div>

            {/* CTA button */}
            <button
              type="button"
              onClick={handleLoginAfterRegistration}
              disabled={isLoggingIn}
              className="w-full bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-[#FFF] font-semibold py-3 rounded-md transition-colors"
            >
              {isLoggingIn ? "Logging in..." : "Login to Dashboard"}
            </button>
          </div>
        </div>
      )}

      <ExistingEmailModal
        open={showExistingEmailModal}
        onOpenChange={setShowExistingEmailModal}
        email={existingEmail}
      />
    </>
  );
};

export default SignUpPage;
